import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';

const MosaicDarkPreset = definePreset(Aura, {
  primitive: {
    // Define custom purple/violet colors based on your gradient
    mosaicPurple: {
      50: '#f8f6ff',
      100: '#f1ebff',
      200: '#e5dbff',
      300: '#d1c0ff',
      400: '#b89aff',
      500: '#9c74ff',
      600: '#806edd', // Your starting gradient color
      700: '#6d5bc4',
      800: '#5c4ab8', // Your ending gradient color
      900: '#4d3d9a',
      950: '#302568',
    },
  },
  semantic: {
    // Use the custom purple palette for primary colors
    primary: {
      50: '{mosaicPurple.50}',
      100: '{mosaicPurple.100}',
      200: '{mosaicPurple.200}',
      300: '{mosaicPurple.300}',
      400: '{mosaicPurple.400}',
      500: '{mosaicPurple.500}',
      600: '{mosaicPurple.600}',
      700: '{mosaicPurple.700}',
      800: '{mosaicPurple.800}',
      900: '{mosaicPurple.900}',
      950: '{mosaicPurple.950}',
    },
    // Configure color scheme for dark mode
    colorScheme: {
      light: {
        // Light mode configuration (fallback)
        primary: {
          color: '{mosaicPurple.600}',
          inverseColor: '#ffffff',
          hoverColor: '{mosaicPurple.700}',
          activeColor: '{mosaicPurple.800}',
        },
        surface: {
          0: '#ffffff',
          50: '{slate.50}',
          100: '{slate.100}',
          200: '{slate.200}',
          300: '{slate.300}',
          400: '{slate.400}',
          500: '{slate.500}',
          600: '{slate.600}',
          700: '{slate.700}',
          800: '{slate.800}',
          900: '{slate.900}',
          950: '{slate.950}',
        },
      },
      dark: {
        // Dark mode configuration with white text everywhere
        primary: {
          color: '{mosaicPurple.500}',
          inverseColor: '#ffffff',
          hoverColor: '{mosaicPurple.400}',
          activeColor: '{mosaicPurple.300}',
          contrastColor: '#ffffff',
        },
        surface: {
          0: '#ffffff',
          50: '{zinc.50}',
          100: '{zinc.100}',
          200: '{zinc.200}',
          300: '{zinc.300}',
          400: '{zinc.400}',
          500: '{zinc.500}',
          600: '{zinc.600}',
          700: '{zinc.700}',
          800: '{zinc.800}',
          900: '{zinc.900}',
          950: '{zinc.950}',
        },
        // Text color definitions for all text elements
        content: {
          color: '#ffffff',
          hoverColor: '#ffffff',
        },
        text: {
          color: '#ffffff',
          hoverColor: '#ffffff',
          mutedColor: 'rgba(255, 255, 255, 0.7)',
          hoveredMutedColor: 'rgba(255, 255, 255, 0.8)',
        },
      },
    },
    // Focus ring styling
    focusRing: {
      width: '2px',
      style: 'solid',
      color: '{primary.color}',
      offset: '2px',
      shadow: '0 0 0 2px rgba(128, 110, 221, 0.2)',
    },
  },
  // Global CSS for PrimeNG-specific styling only
  css: ({ dt }) => `
    /* Root dark mode styling */
    .mosaic-dark {
      background: ${dt('surface.900')};
      color: #ffffff;
    }

    /* Universal text color override for dark mode */
    .mosaic-dark,
    .mosaic-dark *:not(.p-menu-item-link-active):not(.p-menu-item-link-active *) {
      color: #ffffff !important;
    }

    /* Specific overrides for muted text */
    .mosaic-dark .text-muted,
    .mosaic-dark .p-text-secondary,
    .mosaic-dark small,
    .mosaic-dark .small {
      color: rgba(255, 255, 255, 0.7) !important;
    }

    /* PRIMARY BUTTONS - Gradient styling (PrimeNG specific) */
    .mosaic-dark .p-button:not(.p-button-secondary):not(.p-button-outlined):not(.p-button-text) {
      background: linear-gradient(90deg, #806EDD 0%, #5C4AB8 100%);
      border: none;
      color: #ffffff;
    }

    .mosaic-dark .p-button:not(.p-button-secondary):not(.p-button-outlined):not(.p-button-text):hover:not(:disabled) {
      background: linear-gradient(90deg, #9c74ff 0%, #6d5bc4 100%);
    }

    .mosaic-dark .p-button:not(.p-button-secondary):not(.p-button-outlined):not(.p-button-text):active:not(:disabled) {
      background: linear-gradient(90deg, #6d5bc4 0%, #4d3d9a 100%);
    }

    /* Secondary buttons (PrimeNG specific) */
    .mosaic-dark .p-button.p-button-secondary {
      background: transparent;
      border: 1px solid #ffffff;
      color: #ffffff;
    }

    .mosaic-dark .p-button.p-button-secondary:hover:not(:disabled) {
      background: rgba(255, 255, 255, 0.1);
      border-color: #ffffff;
      color: #ffffff;
    }

    .mosaic-dark .p-button.p-button-secondary:active:not(:disabled) {
      background: rgba(255, 255, 255, 0.2);
      border-color: #ffffff;
      color: #ffffff;
    }

    /* Text buttons (PrimeNG specific) */
    .mosaic-dark .p-button.p-button-text {
      background: transparent;
      border: none;
      color: #ffffff;
    }

    .mosaic-dark .p-button.p-button-text:hover:not(:disabled) {
      background: rgba(255, 255, 255, 0.1);
      color: #ffffff;
    }

    /* Outlined buttons (PrimeNG specific) */
    .mosaic-dark .p-button.p-button-outlined {
      background: transparent;
      border: 1px solid ${dt('primary.color')};
      color: ${dt('primary.color')};
    }

    .mosaic-dark .p-button.p-button-outlined:hover:not(:disabled) {
      background: ${dt('primary.color')};
      border-color: ${dt('primary.color')};
      color: #ffffff;
    }

    /* Card styling (PrimeNG specific) */
    .mosaic-dark .p-card {
      background: ${dt('surface.800')};
      border: 1px solid ${dt('surface.600')};
      color: #ffffff;
    }

    .mosaic-dark .p-card .p-card-title {
      color: #ffffff;
    }

    .mosaic-dark .p-card .p-card-subtitle {
      color: rgba(255, 255, 255, 0.7);
    }

    .mosaic-dark .p-card .p-card-content {
      color: #ffffff;
    }

    /* Form elements (PrimeNG specific) */
    .mosaic-dark .p-inputtext,
    .mosaic-dark .p-dropdown,
    .mosaic-dark .p-multiselect,
    .mosaic-dark .p-textarea,
    .mosaic-dark .p-inputnumber-input,
    .mosaic-dark .p-datepicker input {
      background: ${dt('surface.800')} !important;
      border: 1px solid ${dt('surface.600')} !important;
      color: #ffffff !important;
    }

    .mosaic-dark .p-inputtext:hover,
    .mosaic-dark .p-dropdown:hover,
    .mosaic-dark .p-multiselect:hover,
    .mosaic-dark .p-textarea:hover,
    .mosaic-dark .p-inputnumber:hover .p-inputnumber-input,
    .mosaic-dark .p-datepicker:hover input {
      border-color: ${dt('primary.color')} !important;
    }

    .mosaic-dark .p-inputtext:focus,
    .mosaic-dark .p-dropdown:focus,
    .mosaic-dark .p-multiselect:focus,
    .mosaic-dark .p-textarea:focus,
    .mosaic-dark .p-inputnumber:focus-within .p-inputnumber-input,
    .mosaic-dark .p-datepicker:focus-within input {
      border-color: ${dt('primary.color')} !important;
      box-shadow: 0 0 0 2px rgba(128, 110, 221, 0.2) !important;
      outline: none !important;
    }

    .mosaic-dark .p-inputtext::placeholder,
    .mosaic-dark .p-dropdown-label.p-placeholder,
    .mosaic-dark .p-textarea::placeholder,
    .mosaic-dark .p-inputnumber-input::placeholder,
    .mosaic-dark .p-datepicker input::placeholder {
      color: rgba(255, 255, 255, 0.5) !important;
    }

    /* InputNumber specific styling */
    .mosaic-dark .p-inputnumber {
      background: transparent !important;
    }

    .mosaic-dark .p-inputnumber-button {
      background: ${dt('surface.700')} !important;
      border: 1px solid ${dt('surface.600')} !important;
      color: #ffffff !important;
    }

    .mosaic-dark .p-inputnumber-button:hover {
      background: ${dt('surface.600')} !important;
      border-color: ${dt('primary.color')} !important;
    }

    /* DatePicker specific styling */
    .mosaic-dark .p-datepicker {
      background: transparent !important;
    }

    .mosaic-dark .p-datepicker-trigger {
      background: ${dt('surface.700')} !important;
      border: 1px solid ${dt('surface.600')} !important;
      color: #ffffff !important;
    }

    .mosaic-dark .p-datepicker-trigger:hover {
      background: ${dt('surface.600')} !important;
      border-color: ${dt('primary.color')} !important;
    }

    /* DatePicker panel styling */
    .mosaic-dark .p-datepicker-panel {
      background: ${dt('surface.800')} !important;
      border: 1px solid ${dt('surface.600')} !important;
      color: #ffffff !important;
    }

    .mosaic-dark .p-datepicker table td,
    .mosaic-dark .p-datepicker table th {
      color: #ffffff !important;
    }

    .mosaic-dark .p-datepicker table td > span {
      color: #ffffff !important;
      background: transparent !important;
    }

    .mosaic-dark .p-datepicker table td > span:hover {
      background: ${dt('primary.color')} !important;
      color: #ffffff !important;
    }

    .mosaic-dark .p-datepicker table td.p-datepicker-today > span {
      background: ${dt('surface.600')} !important;
      color: #ffffff !important;
    }

    .mosaic-dark .p-datepicker table td.p-datepicker-selected > span {
      background: ${dt('primary.color')} !important;
      color: #ffffff !important;
    }

    /* Fix for DatePicker selected day visibility - High Specificity to override transparent background */
    .mosaic-dark .p-datepicker table td.p-datepicker-day-selected > span,
    .mosaic-dark .p-datepicker table td.p-highlight > span,
    .mosaic-dark .p-datepicker table td > span.p-datepicker-day-selected {
      background-color: ${dt('primary.color')} !important;
      color: #ffffff !important;
    }

    .mosaic-dark .p-datepicker table td.p-datepicker-day-selected:hover > span,
    .mosaic-dark .p-datepicker table td.p-highlight:hover > span,
    .mosaic-dark .p-datepicker table td > span.p-datepicker-day-selected:hover {
      background-color: ${dt('primary.hoverColor')} !important;
    }

    .mosaic-dark .p-datepicker-header {
      background: ${dt('surface.700')} !important;
      color: #ffffff !important;
    }

    .mosaic-dark .p-datepicker-header .p-datepicker-title {
      color: #ffffff !important;
    }

    .mosaic-dark .p-datepicker-header button {
      color: #ffffff !important;
    }

    .mosaic-dark .p-datepicker-header button:hover {
      background: ${dt('surface.600')} !important;
      color: #ffffff !important;
    }

    /* Skeleton loading styling */
    .mosaic-dark .p-skeleton {
      background: ${dt('surface.700')} !important;
      background: linear-gradient(90deg, ${dt('surface.700')} 25%, ${dt('surface.600')} 50%, ${dt(
    'surface.700'
  )} 75%) !important;
      background-size: 200% 100% !important;
      animation: skeleton-loading 1.5s infinite !important;
    }

    @keyframes skeleton-loading {
      0% { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }

    /* Custom gradient utilities for dark mode */
    .mosaic-dark .bg-gradient-primary {
      background: linear-gradient(90deg, #806EDD 0%, #5C4AB8 100%) !important;
    }

    /* Ensure all text in forms is visible */
    .mosaic-dark input,
    .mosaic-dark textarea,
    .mosaic-dark select {
      color: #ffffff !important;
      background: ${dt('surface.800')} !important;
    }

    .mosaic-dark input[type="number"] {
      color: #ffffff !important;
      background: ${dt('surface.800')} !important;
    }

    /* Fix any remaining gray text issues */
    .mosaic-dark .text-gray-100,
    .mosaic-dark .text-gray-200,
    .mosaic-dark .text-gray-300 {
      color: rgba(255, 255, 255, 0.9) !important;
    }

    .mosaic-dark .text-gray-400,
    .mosaic-dark .text-gray-500 {
      color: rgba(255, 255, 255, 0.7) !important;
    }

    .mosaic-dark .text-gray-600,
    .mosaic-dark .text-gray-700 {
      color: rgba(255, 255, 255, 0.8) !important;
    }

    /* Dropdown panels (PrimeNG specific) */
    .mosaic-dark .p-dropdown-panel {
      background: ${dt('surface.800')};
      border: 1px solid ${dt('surface.600')};
      color: #ffffff;
    }

    .mosaic-dark .p-dropdown-item {
      color: #ffffff;
    }

    .mosaic-dark .p-dropdown-item:hover {
      background: ${dt('surface.700')};
      color: #ffffff;
    }

    .mosaic-dark .p-dropdown-item.p-dropdown-item-selected {
      background: ${dt('primary.color')};
      color: #ffffff;
    }

    /* Label styling (PrimeNG specific) */
    .mosaic-dark label,
    .mosaic-dark .p-float-label label {
      color: #ffffff;
    }

    .mosaic-dark .p-float-label label.p-label-float {
      color: ${dt('primary.color')};
    }

    /* Ensure links are visible (PrimeNG specific) */
    .mosaic-dark a {
      color: ${dt('primary.color')};
    }

    .mosaic-dark a:hover {
      color: ${dt('primary.hoverColor')};
    }

    /* Menu and navigation items (PrimeNG specific) */
    .mosaic-dark .p-menuitem-link {
      color: #ffffff;
    }

    .mosaic-dark .p-menuitem-link:hover {
      background: rgba(255, 255, 255, 0.1);
      color: #ffffff;
    }

    /* Active menu items with primary color background */
    .mosaic-dark .p-menu-item-link-active {
      background: ${dt('primary.color')} !important;
      color: #ffffff !important;
      border-color: ${dt('primary.color')} !important;
    }

    .mosaic-dark .p-menu-item-link-active .p-menuitem-icon,
    .mosaic-dark .p-menu-item-link-active .p-menuitem-text,
    .mosaic-dark .p-menu-item-link-active span {
      color: #ffffff !important;
    }

    /* Data table styling (PrimeNG specific) */
    .mosaic-dark .p-datatable {
      background: ${dt('surface.800')};
      color: #ffffff;
    }

    .mosaic-dark .p-datatable-header {
      background: ${dt('surface.700')};
      border-color: ${dt('surface.600')};
      color: #ffffff;
    }

    .mosaic-dark .p-datatable-tbody > tr {
      background: ${dt('surface.800')};
      color: #ffffff;
    }

    .mosaic-dark .p-datatable-tbody > tr:hover {
      background: ${dt('surface.700')};
      color: #ffffff;
    }
  `,
});

export default MosaicDarkPreset;
